import { goals as goalsNs, parseDay, type Day, type GoalProgress } from '@map-my-money/engine';
import { useId, useMemo } from 'react';
import { Link } from 'react-router';
import type { VersionSummary } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { now, todayDay } from '../lib/clock.ts';
import {
  compactMoney, dayMonth, daysSpan, longSpan, money, monthYear, percent, shortDate, whenLabel,
} from '../lib/format.ts';
import {
  checkpointsFrom, decode, snapshotNetWorth,
  type CurrentState, type ForecastView, type MonthView, type OwnedAsset, type PlannedAsset,
} from '../lib/model.ts';
import { goalShortfall, goalStatus, goalTone } from '../lib/goalStatus.ts';
import { personName, useActivePlan, useStore } from '../state/store.tsx';
import { MonthChart } from './charts/MonthChart.tsx';
import { Icon } from './Icon.tsx';
import { Delta, ProgressBar, SectionHeader, SourceBadge } from './ui.tsx';

/** Everything still to come this month (R21): its change in cash and in investments, and where the month ends. */
export function RestOfMonthPill({ estimate, monthEnd }: { estimate: { total: number; cash: number; growth: number }; monthEnd: { date: string; net_worth_estimate: string } }) {
  return (
    <span className="rest-of-month">
      <span className="pill-stat">
        {t('home.restOfMonth')}
        <Delta value={estimate.total}>{money(estimate.total, { sign: true })}</Delta>
      </span>
      <span className="rest-of-month__detail">
        {t('home.restOfMonth.detail', {
          cash: money(estimate.cash, { sign: true }), investments: money(estimate.growth, { sign: true }),
          end: money(Number(monthEnd.net_worth_estimate)), date: dayMonth(parseDay(monthEnd.date)),
        })}
      </span>
    </span>
  );
}

/** What now starts from (R19, decision 7): up to date, an older check-in, or the setup estimate. */
export function AnchorLine({ anchor, onCheckIn }: { anchor: { kind: 'check-in' | 'estimate'; date: string }; onCheckIn: (() => void) | null }) {
  const today = todayDay();
  const day = parseDay(anchor.date);
  if (anchor.kind === 'check-in' && day >= today) {
    return <p className="anchor-line anchor-line--current" data-testid="anchor-line"><Icon name="check" size={13} strokeWidth={3} />{t('home.anchor.upToDate')}</p>;
  }
  const [text, action] = anchor.kind === 'check-in'
    ? [t('home.anchor.checkIn', { date: shortDate(day, today) }), t('home.anchor.checkInAction')]
    : [t('home.anchor.estimate', { date: shortDate(day, today) }), t('home.anchor.estimateAction')];
  return (
    <p className="anchor-line" data-testid="anchor-line">
      {text}
      {' · '}
      {onCheckIn ? <button type="button" className="link-button" onClick={onCheckIn}>{action}</button> : action}
    </p>
  );
}

type MonthItem = { label: string; amount: number; type: string; date: Day };

function MonthItems({ title, items }: { title: string; items: readonly MonthItem[] }) {
  return (
    <>
      <span className="this-month__upcoming-label">{title}</span>
      <ul className="upcoming-list">
        {items.map((u) => (
          <li key={`${u.date}-${u.label}`}>
            <span className={`dot dot--${u.type}`} aria-hidden="true" />
            <span className="upcoming-list__label">{u.label}</span>
            <span className="upcoming-list__date">{dayMonth(u.date)}</span>
            <span className={`upcoming-list__amount ${u.amount >= 0 ? 'text-mint' : ''}`}>{money(u.amount, { sign: true })}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * "This month": where cash is now and where it lands at month end. `compact` is the desktop
 * top-row card (title inside, the next payments listed under the chart); the default is the
 * mobile section.
 */
export function ThisMonthCard({ month, endCash, variant = 'section' }: { month: MonthView; endCash: number; variant?: 'section' | 'compact' }) {
  const id = useId();
  const upcoming: MonthItem[] = month.points.filter((p) => p.projected).flatMap((p) => p.labels.map((l) => ({ ...l, date: p.date })));
  const soFar: MonthItem[] = month.points
    .filter((p) => !p.projected)
    .flatMap((p) => p.labels.map((l) => ({ ...l, date: p.date })))
    .reverse();
  const change = endCash - month.todayValue;
  const foot = (
    <div className="this-month__foot">
      <div className="stat-mini">
        <span className="stat-mini__label">{t('home.thisMonth.startOn', { date: dayMonth(month.periodStart) })}</span>
        <span className="stat-mini__value">{money(month.startValue)}</span>
      </div>
      <div className="stat-mini stat-mini--center">
        <span className="stat-mini__label">{t('home.thisMonth.now')}</span>
        <span className="stat-mini__value">{money(month.todayValue)}</span>
      </div>
      <div className="stat-mini stat-mini--end">
        <span className="stat-mini__label">{t('home.thisMonth.cashOn', { date: dayMonth(month.periodEnd) })}</span>
        <span className="stat-mini__value">
          <Delta value={change}>{money(endCash)}</Delta>
        </span>
      </div>
    </div>
  );

  if (variant === 'compact') {
    const next = upcoming.slice(0, 3);
    const recent = soFar.slice(0, Math.max(0, 3 - next.length));
    return (
      <section className="card card--bento this-month this-month--compact" aria-labelledby={id}>
        <div className="section-header">
          <h2 className="eyebrow" id={id}>{t('home.thisMonth')}</h2>
          <span className="section-header__meta">{t('home.thisMonth.range', { start: dayMonth(month.periodStart), end: dayMonth(month.periodEnd) })}</span>
        </div>
        <MonthChart month={month} height={150} />
        {foot}
        <div className="this-month__lists">
          <div className="this-month__list">
            {next.length > 0 ? <MonthItems title={t('home.thisMonth.nextUp')} items={next} /> : (
              <>
                <span className="this-month__upcoming-label">{t('home.thisMonth.nextUp')}</span>
                <span className="muted small">{t('home.thisMonth.nothingDue')}</span>
              </>
            )}
          </div>
          {recent.length > 0 && (
            <div className="this-month__list">
              <MonthItems title={t('home.thisMonth.soFar')} items={recent} />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="this-month" aria-labelledby={id}>
      <SectionHeader title={t('home.thisMonth')} id={id} />
      <div className="card card--pad-md this-month__card">
        <MonthChart month={month} />
        {foot}
      </div>
    </section>
  );
}

export function useWaypointSummary(current: CurrentState) {
  const { plan, waypoints } = useStore();
  return useMemo(() => {
    if (!plan) return null;
    const checkpoints = checkpointsFrom(decode(plan), waypoints);
    const last = checkpoints[checkpoints.length - 1];
    if (!last) return { count: 0, days: null, date: null, delta: null };
    const days = todayDay() - last.date;
    return { count: checkpoints.length, days, date: last.date, delta: current.netWorth - snapshotNetWorth(last) };
  }, [plan, waypoints, current.netWorth]);
}

export function LogWaypointCard({ current, onOpen, showCount = true }: { current: CurrentState; onOpen: () => void; showCount?: boolean }) {
  const summary = useWaypointSummary(current);
  const { canEdit } = useActivePlan();
  if (!summary) return null;
  return (
    <button type="button" className="waypoint-cta" disabled={!canEdit} onClick={onOpen}>
        <span className="waypoint-cta__check" aria-hidden="true"><Icon name="check" size={18} strokeWidth={3} /></span>
        <span className="waypoint-cta__text">
          <span className="waypoint-cta__title">{t('home.logWaypoint')}</span>
          <span className="waypoint-cta__sub">
            {summary.days === null || summary.date === null ? t('home.logWaypoint.first')
              : summary.days === 0 ? t('home.logWaypoint.today')
                : t('home.logWaypoint.changes', { span: summary.days >= 60 ? longSpan(Math.round(summary.days / 30.44)) : daysSpan(summary.days), date: shortDate(summary.date, todayDay()) })}
          </span>
        </span>
        {summary.delta !== null && summary.days !== 0 && (
          <span className="waypoint-cta__delta"><Delta value={summary.delta}>{money(Math.abs(summary.delta))}</Delta></span>
        )}
        {!showCount && summary.days !== 0 && <Icon name="chevronRight" size={16} className="waypoint-cta__chevron" />}
      {showCount && summary.days !== null && (
        <span className="waypoint-cta__badge" title={t('home.logWaypoint.daysBadge', { n: summary.days })}>
          <span aria-hidden="true">{summary.days}</span>
          <span className="sr-only">{t('home.logWaypoint.daysBadge', { n: summary.days })}</span>
        </span>
      )}
    </button>
  );
}

export function WaypointsHeader({ count }: { count: number }) {
  const id = useId();
  return (
    <div className="section-header" id={id}>
      <h2 className="eyebrow">{t('home.waypoints')}</h2>
      <span className="section-header__meta" aria-label={t('home.waypoints.count', { n: count })}>
        <span aria-hidden="true">{count}</span>
        <Link to="/forecast" className="icon-chip" aria-label={t('nav.forecast')}><Icon name="trend" size={13} /></Link>
      </span>
    </div>
  );
}

/**
 * A goal's status in its tone, then its shortfall on a quieter line of its own. With a "by" day each
 * " · " part stays whole, so a wrap falls between parts; `trail` follows the status on its line.
 */
export function GoalStatus({ goal, trail }: { goal: GoalProgress; trail?: string }) {
  const text = goalStatus(goal);
  const parts = text.split(' · ');
  const short = goalShortfall(goal);
  return (
    <>
      <span className={['goal-status__when', goalTone(goal)].filter(Boolean).join(' ')}>
        {goal.deadline
          ? parts.map((part, i) => (
            <span key={i}>
              <span className="nowrap">{i < parts.length - 1 ? `${part} ·` : part}</span>
              {i < parts.length - 1 && ' '}
            </span>
          ))
          : text}
      </span>
      {trail}
      {short && <span className="goal-status__short">{short}</span>}
    </>
  );
}

export function NorthStarCard({ view, level = 'h2' }: { view: ForecastView; level?: 'h2' | 'h3' }) {
  const id = useId();
  const goal = goalsNs.northStar(view.goals);
  return (
    <section className="card card--pad-lg north-star" aria-labelledby={id}>
      <div className="section-header">
        {level === 'h2' ? <h2 className="eyebrow" id={id}>{t('home.northStar')}</h2> : <h3 className="eyebrow" id={id}>{t('home.northStar')}</h3>}
        <Link to="/goals" className="text-link">{t('common.manage')}</Link>
      </div>
      {!goal ? (
        <p className="muted">{t('home.northStar.none')}</p>
      ) : (
        <>
          <div className="north-star__row">
            <span className="north-star__label">{goal.goal.label}</span>
            <span className="north-star__status"><GoalStatus goal={goal} /></span>
          </div>
          <ProgressBar percent={goal.progressPercent} label={t('goals.progress', { pct: Math.round(goal.progressPercent) })} />
          <div className="north-star__scale">
            <span>{compactMoney(goal.goal.startingValue)}</span>
            <span className="text-mint strong">{percent(goal.progressPercent, { digits: 1 })}</span>
            <span>{compactMoney(goal.targetValue)}</span>
          </div>
        </>
      )}
    </section>
  );
}

export function OwnedList({ assets, planned = [], variant }: { assets: readonly OwnedAsset[]; planned?: readonly PlannedAsset[]; variant: 'cards' | 'rows' }) {
  if (assets.length === 0 && planned.length === 0) return <p className="muted">{t('home.whatYouOwn.none')}</p>;
  const icon = (assetClass: string) => <span className="owned__icon" aria-hidden="true"><Icon name={assetClass === 'vehicle' ? 'car' : 'home'} size={18} /></span>;
  return (
    <ul className={`owned owned--${variant}`}>
      {assets.map((a) => (
        <li key={a.id}>
          <Link to={`/plan?item=asset:${a.id}`} className="owned__item">
            {icon(a.assetClass)}
            <span className="owned__label">{a.label}</span>
            <span className="owned__value">{compactMoney(a.value)}</span>
            <span className={`owned__pct ${a.changePercent < 0 ? 'owned__pct--down' : ''}`} title={t('home.whatYouOwn.sinceBought', { pct: percent(a.changePercent, { sign: true }) })}>
              {percent(a.changePercent, { sign: true })}
            </span>
            {variant === 'cards' && <Icon name="chevronRight" size={16} className="owned__chevron" />}
          </Link>
        </li>
      ))}
      {planned.map((a) => (
        <li key={a.id} data-testid="owned-planned">
          <Link to={`/plan?item=asset:${a.id}`} className="owned__item owned__item--planned">
            {icon(a.assetClass)}
            <span className="owned__label">
              {a.label}
              <span className="owned__planned">{t('plan.planned', { month: monthYear(a.purchaseDate) })}</span>
            </span>
            <span className="owned__value">{compactMoney(a.price)}</span>
            {variant === 'cards' && <Icon name="chevronRight" size={16} className="owned__chevron" />}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Plan versions and waypoints, newest first; the latest plan change can be undone. */
/** `since` narrows the list to plan versions after that one (a chat link's "See what changed"). */
export function RecentChanges({ limit = 4, showAll = false, since = null }: { limit?: number; showAll?: boolean; since?: number | null }) {
  const { versions, waypoints, plan, restore, saving, me } = useStore();
  const { canEdit, plan: active } = useActivePlan();
  const together = !!active && (active.role !== 'owner' || (active.memberCount ?? 0) > 0);
  const byLine = (v: VersionSummary) => {
    if (!together || !v.author) return null;
    return v.author.userId === me?.user.id ? t('history.byYou') : t('history.by', { name: personName(v.author) });
  };
  const items = useMemo(() => {
    if (!plan) return [];
    const checkpoints = checkpointsFrom(decode(plan), waypoints);
    const byId = new Map(checkpoints.map((c, i) => [c.id, { c, prev: checkpoints[i - 1] }]));
    const wp = waypoints.map((w) => {
      const entry = byId.get(w.id);
      const delta = entry && entry.prev ? snapshotNetWorth(entry.c) - snapshotNetWorth(entry.prev) : null;
      return { kind: 'waypoint' as const, key: `w-${w.id}`, at: w.createdAt, source: w.source, delta };
    });
    const vs = versions.map((v, i) => ({ kind: 'version' as const, key: `v-${v.version}`, at: v.createdAt, source: v.source, v, isHead: i === 0 }));
    const all = since === null ? [...vs, ...wp] : vs.filter((item) => item.v.version > since);
    return all.sort((a, b) => b.at.localeCompare(a.at)).slice(0, showAll ? 50 : limit);
  }, [versions, waypoints, plan, limit, showAll, since]);

  if (items.length === 0) return <p className="muted">{since === null ? t('home.recentChanges.none') : t('history.sinceNone', { version: since })}</p>;
  const clock = now();
  return (
    <ul className="changes">
      {items.map((item) => (
        <li key={item.key} className="changes__item">
          <SourceBadge source={item.source} />
          <div className="changes__body">
            <span className="changes__summary">
              {item.kind === 'version'
                ? item.v.summary
                : t('home.waypointLogged', { delta: item.delta === null ? '' : compactMoney(item.delta, { sign: true }) }).replace(/ · $/, '')}
            </span>
            <span className="changes__meta">
              {whenLabel(item.at, clock)}
              {item.kind === 'version' && <ByLine text={byLine(item.v)} />}
              {item.kind === 'version' && item.isHead && item.v.version > 1 && (
                <>
                  {' · '}
                  <button type="button" className="link-button" disabled={saving || !canEdit} onClick={() => void restore(item.v.version - 1)}>
                    {t('common.undo')}
                  </button>
                </>
              )}
              {showAll && item.kind === 'version' && !item.isHead && (
                <>
                  {' · '}
                  <button type="button" className="link-button" disabled={saving || !canEdit} onClick={() => void restore(item.v.version)} aria-label={`${t('common.restore')} ${item.v.summary}`}>
                    {t('common.restore')}
                  </button>
                </>
              )}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ByLine({ text }: { text: string | null }) {
  return text ? <span className="changes__by">{' · '}{text}</span> : null;
}

/** Debt free (every debt but the mortgage) and mortgage paid off, each named, so the two dates never read as a contradiction. */
export function DebtDates({ debtFree, mortgageFree, level = 'h2' }: { debtFree: string | null; mortgageFree: string | null; level?: 'h2' | 'h3' }) {
  const id = useId();
  if (!debtFree && !mortgageFree) return null;
  return (
    <section className="card card--pad-lg" aria-labelledby={id} data-testid="debt-dates">
      {level === 'h2'
        ? <h2 className="eyebrow key-numbers__title" id={id}>{t('debts.title')}</h2>
        : <h3 className="eyebrow key-numbers__title" id={id}>{t('debts.title')}</h3>}
      <dl className="key-numbers">
        {debtFree && (
          <div className="key-numbers__row"><dt>{t('debts.debtFree')}</dt><dd>{monthYear(parseDay(debtFree))}</dd></div>
        )}
        {mortgageFree && (
          <div className="key-numbers__row"><dt>{t('debts.mortgageFree')}</dt><dd>{monthYear(parseDay(mortgageFree))}</dd></div>
        )}
      </dl>
    </section>
  );
}
